/**
 * Calibration Service - API calls for the 4-phase calibration workflow
 * 
 * Phases:
 * 1. Pre-calibration: Managers submit KPA motivations
 * 2. Report generation: One-up manager reviews integrated report
 * 3. Calibration session: Live discussion with rating adjustments
 * 4. Post-calibration: Analytics, confirmation, rating activation
 * 
 * Reference: Manager-Journey.md #650, OneUp-HRBP-Workflow-Requirements.md
 */

import { API_CONFIG } from '@/config/api'
import { authenticatedFetch } from '../authenticatedFetch'

// ============================================================================
// TYPES
// ============================================================================

/** Error carrying the server's own status and refusal text. */
export interface CalibrationApiError extends Error {
  status: number
  detail: string
}

export type CalibrationType = 'mid-year' | 'year-end'

export type CalibrationSessionStatus = 
  | 'scheduled' 
  | 'pre-calibration' 
  | 'report-generation' 
  | 'in-progress' 
  | 'completed' 
  | 'cancelled'

export type ParticipantRole = 'manager' | 'one-up-manager' | 'hrbp' | 'observer'

export type PreCalibrationStatus = 'draft' | 'submitted' | 'reviewed' | 'flagged'

export type PerformanceRating = 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'

export type RatingVisibilityStatus = 'hidden' | 'pending-discussion' | 'confirmed' | 'active'

// ============================================================================
// INTERFACES
// ============================================================================

export interface CalibrationParticipant {
  id: string
  user_id: string
  role: ParticipantRole
  invited_at: string
  joined_at?: string
  user_name?: string
  user_role?: string
}

export interface CalibrationSession {
  id: string
  type: CalibrationType
  review_year: number
  scheduled_date: string
  status: CalibrationSessionStatus
  facilitator_id?: string
  // Facilitator's display name — labels a session the viewer submits INTO by
  // whose session it is ("to Sam E."), since the roster there is their peers.
  facilitator_name?: string
  organizational_unit_id?: string
  organizational_unit_name?: string
  notes?: string
  reviews_to_calibrate: number
  adjustments_made: number
  // Per-session submission summary from list_calibration_sessions (so cards
  // render each session's own data without fetching submissions per card).
  submission_count?: number
  pending_calibration_count?: number
  manager_names?: string[]
  rating_distribution?: Record<string, number>
  viewer_has_submission?: boolean
  // Expected People Leaders to submit into this session (direct reports of the
  // facilitator who manage a team) — the real "X of N submitted" denominator,
  // plus the roster so the UI can list who is still pending.
  expected_submitter_count?: number
  expected_submitters?: {
    id: string
    name: string
    submitted?: boolean
    // Declaration-gate state: Generate Report unlocks when every expected PL is
    // declared (non-stale) or waived.
    declared?: boolean
    // Their declaration lapsed — an awaiting-checkin exclusion's member has since
    // completed their check-in; the PL must submit/exclude them and re-confirm.
    declaration_stale?: boolean
    waived?: boolean
    // Whom this People Leader presents in THIS session: a stand-in presents a vacant
    // seat's people to its One-Up and their own team to their own (28 Sep 2026). Null
    // when the directory was unreachable.
    member_ids?: string[] | null
  }[]
  // Generate Report is blocked until pending_declaration_count reaches 0.
  declared_submitter_count?: number
  pending_declaration_count?: number
  // Conscious member exclusions (visible rows in the One-Up grid).
  // reason_text is facilitator-only; null for everyone else.
  member_exclusions?: MemberExclusion[]
  // Expected People Leaders who haven't submitted yet — Generate Report is blocked until 0.
  pending_submitter_count?: number
  report_generated_at?: string
  started_at?: string
  completed_at?: string
  created_at: string
  updated_at: string
  participants: CalibrationParticipant[]
}

export type ExclusionReasonCode =
  | 'exiting'
  | 'secondment'
  | 'transferred-out'
  | 'not-reviewable'
  | 'dispute'
  | 'awaiting-checkin'
  | 'other'

export interface MemberExclusion {
  employee_id: string
  employee_name?: string | null
  manager_id: string
  manager_name?: string
  reason_code: ExclusionReasonCode
  reason_text?: string | null
}

export interface MemberExclusionCreate {
  manager_id: string
  employee_id: string
  employee_name?: string
  reason_code: ExclusionReasonCode
  reason_text?: string
}

export interface SubmitterDeclaration {
  id: string
  session_id: string
  manager_id: string
  declared_at: string
  declared_by: string
  covered_member_ids?: string[]
  coverage_verified: boolean
  exclusions: (MemberExclusionCreate & { id: string })[]
}

export interface SubmitterWaiver {
  id: string
  session_id: string
  manager_id: string
  waived_by: string
  reason: string
  waived_at: string
  uncovered_member_ids?: string[] | null
}

export interface BatchSubmissionResult {
  created: number
  failed: number
  results: { employee_id: string; ok: boolean; submission_id?: string; error?: string }[]
}

export interface CalibrationSessionCreate {
  type: CalibrationType
  review_year: number
  scheduled_date: string
  facilitator_id?: string
  organizational_unit_id?: string
  organizational_unit_name?: string
  notes?: string
  // Explicit manager roster — the specific managers to calibrate in this session.
  // Omit/empty for a legacy org-wide session (all the facilitator's report-managers).
  participants?: { user_id: string; role: ParticipantRole }[]
}

/** A One-Up HR can schedule a calibration session for (HR UAT list #25). */
export interface CalibrationChair {
  id: string
  name: string
  people_leaders: number
  reports_to_assess: number
  /** Sessions of this type already scheduled for the year. */
  sessions: number
}

/** A manager a one-up can calibrate when scheduling a session. */
export interface AvailableManager {
  id: string
  name: string
  // Where this manager currently sits (a roster session OR a legacy roster-less
  // session) for this facilitator+type+year. Null = free to assign.
  assigned_session_id?: string | null
  assigned_session_date?: string | null
  // True once they've SUBMITTED ratings in that session -> LOCKED (can't be moved).
  // Assigned but not submitted -> MOVABLE (selecting them moves them to the new session).
  submitted?: boolean
}

export interface CalibrationSessionUpdate {
  scheduled_date?: string
  status?: CalibrationSessionStatus
  facilitator_id?: string
  notes?: string
  // When provided, REPLACES the session's People-Leader roster (reschedule can add
  // or deselect). Omit to leave the roster unchanged. People Leaders who've already
  // submitted are always kept server-side regardless of what's sent.
  participants?: { user_id: string; role: ParticipantRole }[]
}

export interface KPAMotivation {
  id?: string
  kpa_id?: string
  kpa_name: string
  kpa_rating?: PerformanceRating
  motivation: string
}

export interface PreCalibrationSubmission {
  id: string
  session_id: string
  manager_id: string
  employee_id: string
  proposed_rating: PerformanceRating
  overall_motivation: string
  evidence_references?: string[]
  status: PreCalibrationStatus
  submitted_at?: string
  reviewed_at?: string
  flagged: boolean
  flag_reason?: string
  created_at: string
  updated_at: string
  kpa_motivations: KPAMotivation[]
  manager_name?: string
  employee_name?: string
  employee_role?: string
  // Calibration fields
  calibrated_rating?: PerformanceRating
  calibration_notes?: string
  calibrated_by?: string
  calibrated_at?: string
}

export interface PreCalibrationSubmissionCreate {
  manager_id: string
  employee_id: string
  proposed_rating: PerformanceRating
  overall_motivation: string
  evidence_references?: string[]
  kpa_motivations: KPAMotivation[]
  status?: PreCalibrationStatus  // If provided, use this status instead of 'draft'
  // Denormalized names for display
  manager_name?: string
  employee_name?: string
  employee_role?: string
}

export interface RatingAdjustment {
  id: string
  session_id: string
  employee_id: string
  original_manager_id: string
  original_rating: PerformanceRating
  adjusted_rating: PerformanceRating
  adjustment_reason: string
  adjusted_by: string
  adjusted_by_role: ParticipantRole
  adjusted_at: string
  visibility_status: RatingVisibilityStatus
  discussion_scheduled_for?: string
  discussion_notes?: string
  manager_confirmed: boolean
  manager_confirmation_date?: string
  employee_visible_date?: string
  employee_name?: string
  original_manager_name?: string
  adjusted_by_name?: string
}

export interface RatingAdjustmentCreate {
  employee_id: string
  original_manager_id: string
  original_rating: PerformanceRating
  adjusted_rating: PerformanceRating
  adjustment_reason: string
  adjusted_by: string
  adjusted_by_role: ParticipantRole
}

export interface CalibrationReport {
  session_id: string
  generated_at: string
  total_submissions: number
  submitted: number
  pending: number
  flagged_count: number
  rating_distribution: {
    exceptional: number
    proficient: number
    developing: number
    inadequate: number
  }
  flagged_employees: Array<{
    employee_id: string
    reason: string
  }>
}

export interface CalibrationAnalytics {
  session_id: string
  total_reviews: number
  total_adjustments: number
  adjustment_percentage: number
  pre_distribution: Record<string, number>
  post_distribution: Record<string, number>
  adjustments_up: number
  adjustments_down: number
  consistency_score?: number
  cross_team_variance?: number
  pending_discussions: number
  confirmed_discussions: number
  generated_at: string
}

export interface CalibrationSessionListResponse {
  sessions: CalibrationSession[]
  total: number
  limit: number
  offset: number
  /** Whether this caller may ask for every session (the signed-in list: admin, hr_admin). */
  can_view_all?: boolean
  /** What the server applied: 'mine' even if 'all' was asked for. */
  scope?: 'mine' | 'all'
}

// ============================================================================
// SERVICE
// ============================================================================

class CalibrationService {
  private baseURL = API_CONFIG.baseURL

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers as Record<string, string>),
      },
    })

    if (!response.ok) {
      // Carry the server's `status` and `detail` on the error. Callers used to
      // read `err.response.status` — an axios shape this fetch-based client
      // never produces — so every branch keyed on a status code silently fell
      // through to a generic toast showing the raw "HTTP error! status: 409 -
      // {...json...}" string. The roster-drift confirm dialog was one of them.
      const raw = await response.text()
      let detail = raw
      try {
        const parsed = JSON.parse(raw)
        detail = typeof parsed?.detail === 'string'
          ? parsed.detail
          : Array.isArray(parsed?.detail)
            ? parsed.detail.map((d: { msg?: string }) => d?.msg).filter(Boolean).join('; ') || raw
            : raw
      } catch {
        /* not JSON — keep the raw body */
      }
      const err = new Error(detail) as CalibrationApiError
      err.status = response.status
      err.detail = detail
      throw err
    }

    if (response.status === 204) {
      return null as T
    }

    return response.json()
  }

  // ============================================================================
  // CALIBRATION SESSIONS
  // ============================================================================

  /**
   * List calibration sessions with optional filters
   */
  async listSessions(params?: {
    year?: number
    type?: CalibrationType
    status?: CalibrationSessionStatus
    org_unit_id?: string
    /** Scope results to sessions this user facilitates or participates in. Leaving it
     *  off is not how to see every session: that is `scope: 'all'`. */
    viewer_id?: string
    /** 'mine', or 'all': every session, which the signed-in list gives only to a caller
     *  entitled to it, as the Annual I&G Review's does. */
    scope?: 'mine' | 'all'
    /** The chair's or a seated person's name or personnel number (at most 100 characters). */
    q?: string
    limit?: number
    offset?: number
  }): Promise<CalibrationSessionListResponse> {
    const searchParams = new URLSearchParams()
    if (params?.year) searchParams.set('year', params.year.toString())
    if (params?.type) searchParams.set('type', params.type)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.org_unit_id) searchParams.set('org_unit_id', params.org_unit_id)
    if (params?.viewer_id) searchParams.set('viewer_id', params.viewer_id)
    if (params?.scope) searchParams.set('scope', params.scope)
    if (params?.q) searchParams.set('q', params.q)
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.offset) searchParams.set('offset', params.offset.toString())
    
    const query = searchParams.toString()
    return this.request<CalibrationSessionListResponse>(
      `/performance/calibration/sessions${query ? `?${query}` : ''}`
    )
  }

  /**
   * Managers a one-up can calibrate when scheduling a session — the facilitator's
   * direct-report managers, each flagged if already rostered into another live
   * session of the same type+year (so the schedule dialog excludes them and the
   * one-up can split their managers across multiple sessions).
   */
  async getAvailableManagers(
    facilitatorId: string,
    type: CalibrationType,
    reviewYear: number
  ): Promise<{ managers: AvailableManager[] }> {
    const params = new URLSearchParams({
      facilitator_id: facilitatorId,
      type,
      review_year: reviewYear.toString(),
    })
    return this.request<{ managers: AvailableManager[] }>(
      `/performance/calibration/sessions/available-managers?${params.toString()}`
    )
  }

  /**
   * The One-Ups HR can schedule a session for: HR schedules on a One-Up's behalf,
   * the One-Up stays the chair (HR only).
   */
  async listChairs(type: CalibrationType, reviewYear: number): Promise<{ chairs: CalibrationChair[] }> {
    const params = new URLSearchParams({ type, review_year: reviewYear.toString() })
    return this.request<{ chairs: CalibrationChair[] }>(
      `/performance/calibration/sessions/chairs?${params.toString()}`
    )
  }

  /**
   * Get a specific calibration session
   */
  async getSession(sessionId: string): Promise<CalibrationSession> {
    return this.request<CalibrationSession>(
      `/performance/calibration/sessions/${sessionId}`
    )
  }

  /**
   * Create a new calibration session
   */
  async createSession(data: CalibrationSessionCreate): Promise<CalibrationSession> {
    return this.request<CalibrationSession>(
      '/performance/calibration/sessions',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  }

  /**
   * Update a calibration session
   */
  async updateSession(sessionId: string, data: CalibrationSessionUpdate): Promise<CalibrationSession> {
    return this.request<CalibrationSession>(
      `/performance/calibration/sessions/${sessionId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    )
  }

  /**
   * Delete (cancel) a calibration session
   * Only scheduled sessions can be deleted by default.
   * Use force=true to delete completed sessions (for testing).
   */
  async deleteSession(sessionId: string, force: boolean = false): Promise<void> {
    const params = force ? '?force=true' : ''
    await this.request<void>(
      `/performance/calibration/sessions/${sessionId}${params}`,
      {
        method: 'DELETE',
      }
    )
  }

  /**
   * Delete a pre-calibration submission (for testing/reset)
   */
  async deleteSubmission(submissionId: string): Promise<{ message: string; submission_id: string }> {
    return this.request(`/performance/calibration/submissions/${submissionId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Begin pre-calibration phase (SCHEDULED → PRE_CALIBRATION)
   */
  async beginPreCalibration(sessionId: string): Promise<{ message: string; session_id: string; status: string }> {
    return this.request(`/performance/calibration/sessions/${sessionId}/begin-pre-calibration`, {
      method: 'POST',
    })
  }

  /**
   * Start a calibration session (REPORT_GENERATION → IN_PROGRESS)
   */
  async startSession(sessionId: string): Promise<{ message: string; session_id: string; status: string }> {
    return this.request(`/performance/calibration/sessions/${sessionId}/start`, {
      method: 'POST',
    })
  }

  /**
   * Complete a calibration session
   */
  async completeSession(sessionId: string): Promise<{ message: string; session_id: string; status: string }> {
    return this.request(`/performance/calibration/sessions/${sessionId}/complete`, {
      method: 'POST',
    })
  }

  /**
   * Generate integrated calibration report (Phase 2).
   * confirmDrift acknowledges members who joined a team AFTER their People
   * Leader declared complete (the server 409s with their names otherwise).
   */
  async generateReport(sessionId: string, opts?: { confirmDrift?: boolean }): Promise<CalibrationReport> {
    const query = opts?.confirmDrift ? '?confirm_drift=true' : ''
    return this.request<CalibrationReport>(
      `/performance/calibration/sessions/${sessionId}/generate-report${query}`,
      { method: 'POST' }
    )
  }

  // ============================================================================
  // SUBMITTER DECLARATIONS / EXCLUSIONS / WAIVERS
  // ============================================================================

  /** The PL's "my submissions are complete" attestation (exclusions upserted atomically). */
  async declareSubmissionsComplete(
    sessionId: string,
    managerId: string,
    exclusions: MemberExclusionCreate[] = []
  ): Promise<SubmitterDeclaration> {
    return this.request<SubmitterDeclaration>(
      `/performance/calibration/sessions/${sessionId}/declarations`,
      { method: 'POST', body: JSON.stringify({ manager_id: managerId, exclusions }) }
    )
  }

  /** Withdraw the declaration so submissions/exclusions can be amended. */
  async withdrawDeclaration(sessionId: string, managerId: string): Promise<void> {
    await this.request(
      `/performance/calibration/sessions/${sessionId}/declarations/${managerId}`,
      { method: 'DELETE' }
    )
  }

  async upsertExclusion(sessionId: string, data: MemberExclusionCreate): Promise<MemberExclusion> {
    return this.request<MemberExclusion>(
      `/performance/calibration/sessions/${sessionId}/exclusions`,
      { method: 'POST', body: JSON.stringify(data) }
    )
  }

  async deleteExclusion(sessionId: string, employeeId: string): Promise<void> {
    await this.request(
      `/performance/calibration/sessions/${sessionId}/exclusions/${employeeId}`,
      { method: 'DELETE' }
    )
  }

  /** Facilitator waives an unresponsive PL so the session can proceed. */
  async waiveSubmitter(sessionId: string, managerId: string, waivedBy: string, reason: string): Promise<SubmitterWaiver> {
    return this.request<SubmitterWaiver>(
      `/performance/calibration/sessions/${sessionId}/waivers`,
      { method: 'POST', body: JSON.stringify({ manager_id: managerId, waived_by: waivedBy, reason }) }
    )
  }

  async deleteWaiver(sessionId: string, managerId: string): Promise<void> {
    await this.request(
      `/performance/calibration/sessions/${sessionId}/waivers/${managerId}`,
      { method: 'DELETE' }
    )
  }

  /** Bulk pre-calibration submit — one request, per-row results. */
  async createSubmissionsBatch(
    sessionId: string,
    submissions: PreCalibrationSubmissionCreate[]
  ): Promise<BatchSubmissionResult> {
    return this.request<BatchSubmissionResult>(
      `/performance/calibration/sessions/${sessionId}/submissions/batch`,
      { method: 'POST', body: JSON.stringify({ submissions }) }
    )
  }

  /**
   * Export the session's current ratings as an Excel (.xlsx) workbook and trigger
   * a browser download. The proposed ratings (+ KPA detail) at Report Generation,
   * and again with the calibrated/final ratings once the session is complete.
   */
  async downloadReport(sessionId: string): Promise<void> {
    const response = await authenticatedFetch(
      `${this.baseURL}/performance/calibration/sessions/${sessionId}/export`,
      { method: 'GET' }
    )
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`)
    }
    const blob = await response.blob()
    const cd = response.headers.get('content-disposition') || ''
    const m = cd.match(/filename="?([^"]+)"?/)
    const filename = m ? m[1] : `Calibration_Ratings_${sessionId}.xlsx`

    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  /**
   * Get calibration analytics for a completed session
   */
  async getAnalytics(sessionId: string): Promise<CalibrationAnalytics> {
    return this.request<CalibrationAnalytics>(
      `/performance/calibration/sessions/${sessionId}/analytics`
    )
  }

  // ============================================================================
  // PRE-CALIBRATION SUBMISSIONS
  // ============================================================================

  /**
   * List pre-calibration submissions for a session
   */
  async listSubmissions(
    sessionId: string,
    params?: { manager_id?: string; status?: PreCalibrationStatus }
  ): Promise<PreCalibrationSubmission[]> {
    const searchParams = new URLSearchParams()
    if (params?.manager_id) searchParams.set('manager_id', params.manager_id)
    if (params?.status) searchParams.set('status', params.status)
    
    const query = searchParams.toString()
    return this.request<PreCalibrationSubmission[]>(
      `/performance/calibration/sessions/${sessionId}/submissions${query ? `?${query}` : ''}`
    )
  }

  /**
   * Create a pre-calibration submission
   */
  async createSubmission(
    sessionId: string,
    data: PreCalibrationSubmissionCreate
  ): Promise<PreCalibrationSubmission> {
    return this.request<PreCalibrationSubmission>(
      `/performance/calibration/sessions/${sessionId}/submissions`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  }

  /**
   * Submit a pre-calibration for one-up manager review
   */
  async submitPreCalibration(submissionId: string): Promise<{ message: string; submission_id: string }> {
    return this.request(`/performance/calibration/submissions/${submissionId}/submit`, {
      method: 'POST',
    })
  }

  /**
   * Flag a submission for discussion
   */
  async flagSubmission(
    submissionId: string,
    flagReason: string,
    flaggedBy: string
  ): Promise<{ message: string; submission_id: string }> {
    const params = new URLSearchParams()
    params.set('flag_reason', flagReason)
    params.set('flagged_by', flaggedBy)
    
    return this.request(`/performance/calibration/submissions/${submissionId}/flag?${params}`, {
      method: 'POST',
    })
  }

  /**
   * Calibrate a submission's rating during in-progress session
   * Only one-up managers and HRBPs can calibrate
   */
  async calibrateSubmission(
    submissionId: string,
    calibratedRating: PerformanceRating,
    calibratedBy: string,
    calibrationNotes?: string,
    kpaMotivations?: { kpa_name: string; kpa_rating: string | null; motivation: string }[]
  ): Promise<{ message: string; submission_id: string; original_rating: string; calibrated_rating: string; changed: boolean }> {
    const params = new URLSearchParams()
    params.set('calibrated_rating', calibratedRating)
    params.set('calibrated_by', calibratedBy)
    if (calibrationNotes) params.set('calibration_notes', calibrationNotes)

    // Per-KPA overrides go in the body (an array can't ride in query params).
    return this.request(`/performance/calibration/submissions/${submissionId}/calibrate?${params}`, {
      method: 'PUT',
      body: kpaMotivations && kpaMotivations.length > 0
        ? JSON.stringify({ kpa_motivations: kpaMotivations })
        : undefined,
    })
  }

  // ============================================================================
  // RATING ADJUSTMENTS
  // ============================================================================

  /**
   * Create a rating adjustment during calibration
   * CRITICAL: Adjustments are NOT immediately visible to employees
   */
  async createAdjustment(
    sessionId: string,
    data: RatingAdjustmentCreate
  ): Promise<RatingAdjustment> {
    return this.request<RatingAdjustment>(
      `/performance/calibration/sessions/${sessionId}/adjustments`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  }

  /**
   * Get rating adjustments pending manager confirmation
   */
  async getPendingConfirmations(managerId: string): Promise<RatingAdjustment[]> {
    return this.request<RatingAdjustment[]>(
      `/performance/calibration/adjustments/pending-confirmation?manager_id=${managerId}`
    )
  }

  /**
   * Schedule a discussion with employee about rating adjustment
   */
  async scheduleDiscussion(
    adjustmentId: string,
    scheduledDate: string
  ): Promise<{ message: string; scheduled_for: string }> {
    const params = new URLSearchParams()
    params.set('scheduled_date', scheduledDate)
    
    return this.request(
      `/performance/calibration/adjustments/${adjustmentId}/schedule-discussion?${params}`,
      { method: 'POST' }
    )
  }

  /**
   * Confirm that discussion with employee has taken place
   * This makes the rating visible to the employee
   */
  async confirmDiscussion(
    adjustmentId: string,
    notes?: string
  ): Promise<{ message: string }> {
    return this.request(
      `/performance/calibration/adjustments/${adjustmentId}/confirm-discussion`,
      {
        method: 'POST',
        body: JSON.stringify({ notes }),
      }
    )
  }

  // ============================================================================
  // TEST DATA (test environments only, for admin and HR admin)
  // ============================================================================

  /**
   * The testers to choose from and, for one of them, what resetting their personas' check-ins and
   * calibration would change. Changes nothing.
   */
  async getTestResetPreview(participant?: string, year?: number): Promise<CalibrationTestResetPreview> {
    const params = new URLSearchParams()
    if (participant) params.set('participant', participant)
    if (year) params.set('year', String(year))
    const query = params.toString()
    return this.request(`/performance/calibration/test-reset${query ? `?${query}` : ''}`)
  }

  /**
   * Cancel a tester's personas' calibration sessions, put their check-ins back to Pending and keep a
   * copy of everything it changes. Refused (409) when a session also holds someone else.
   */
  async resetTestData(participant: string, year?: number): Promise<CalibrationTestResetResult> {
    const params = new URLSearchParams({ participant })
    if (year) params.set('year', String(year))
    return this.request(`/performance/calibration/test-reset?${params}`, { method: 'POST' })
  }
}

/** A calibration session a tester's reset cancels (GET /performance/calibration/test-reset). */
export interface CalibrationTestResetSession {
  id: string
  type: string
  review_year: number
  status: string
  chair_id: string | null
  scheduled_date: string | null
  calendar_invite: boolean
  others: {
    /** Other people's check-ins it published and submissions in it: any of them refuses the reset. */
    check_ins: number
    submissions: number
    /** The personas' check-ins of another year it holds, which the reset would change uncopied: refuses too. */
    uncovered_check_ins: number
    /** Others seated in it, who lose it from their list. Shown, never a refusal. */
    seated: string[]
  }
}

/** One of the personas' own check-ins that goes back to Pending. */
export interface CalibrationTestResetCheckIn {
  id: string
  persona: string
  review_type: string
  status: string
  people_leader_id: string
  calibration_session_id: string | null
}

export interface CalibrationTestResetPlan {
  participant: { id: string; name: string | null }
  review_year: number
  personas: string[]
  check_ins: CalibrationTestResetCheckIn[]
  /** Rows the check-ins carry that go, by table. */
  rows: Record<string, number>
  sessions: CalibrationTestResetSession[]
  refused_sessions: string[]
  can_reset: boolean
}

export interface CalibrationTestResetPreview {
  testers: { id: string; name: string }[]
  review_year: number
  plan: CalibrationTestResetPlan | null
}

export interface CalibrationTestResetResult extends CalibrationTestResetPlan {
  /** performance.<prefix>* tables hold a copy of every row the reset changed. */
  backup_prefix: string | null
  copies: Record<string, number>
  removed: Record<string, number>
  sessions_cancelled: number
  check_ins_given_back: number
}

export const calibrationService = new CalibrationService()
export default calibrationService
